import { useEffect } from "react";
import seo from "../seo/pages.json";

const SITE_NAME = "IDCardGen";
const SITE_IMAGE = "https://idcardgen.com/id-card-gen-logo.png";

/** The SEO entry for a public path from src/seo/pages.json (or undefined). */
export function seoPage(path) {
    return seo.pages.find((p) => p.path === path);
}

/**
 * Sets the document title, meta description, canonical, Open Graph / Twitter
 * tags, robots and <html lang>. Pass `path` to use that page's entry in
 * src/seo/pages.json (the same data scripts/prerender-seo.js writes into the
 * static HTML), or pass the fields directly; explicit fields win.
 */
export default function useSeo({ path, title, description, canonical, keywords, noindex, lang } = {}) {
    const page = path ? seoPage(path) : undefined;
    title = title ?? page?.title;
    description = description ?? page?.description;
    canonical = canonical ?? (page ? seo.site + page.path : undefined);
    noindex = noindex ?? page?.noindex ?? false;
    lang = lang ?? page?.lang ?? "en";

    useEffect(() => {
        if (title) {
            document.title = title;
        }
        document.documentElement.lang = lang;

        const ensureMeta = (name, content, attr = "name") => {
            if (!content) return;
            let el = document.head.querySelector(`meta[${attr}='${name}']`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };

        ensureMeta("description", description);
        ensureMeta("keywords", keywords);
        ensureMeta("robots", noindex ? "noindex,follow" : "index,follow,max-image-preview:large");
        ensureMeta("og:site_name", SITE_NAME, "property");
        ensureMeta("og:type", "website", "property");
        ensureMeta("og:title", title, "property");
        ensureMeta("og:description", description, "property");
        ensureMeta("og:image", SITE_IMAGE, "property");
        if (canonical) ensureMeta("og:url", canonical, "property");
        ensureMeta("twitter:card", "summary_large_image", "name");
        ensureMeta("twitter:title", title, "name");
        ensureMeta("twitter:description", description, "name");
        ensureMeta("twitter:image", SITE_IMAGE, "name");

        let link = document.head.querySelector("link[rel='canonical']");
        if (canonical) {
            if (!link) {
                link = document.createElement("link");
                link.setAttribute("rel", "canonical");
                document.head.appendChild(link);
            }
            link.setAttribute("href", canonical);
        } else if (link && noindex) {
            link.remove();
        }
    }, [title, description, canonical, keywords, noindex, lang]);
}
